/* eslint-disable no-useless-escape */
/**
 * Vue directive for input masking functionality
 * Provides dynamic input formatting based on predefined patterns
 *
 * @fileoverview Input mask directive that supports various token types and dynamic masking
 * @version 1.0.0
 */

/**
 * Predefined token definitions for input masking
 * Each token represents a different character validation pattern
 *
 * Available tokens:
 * - '#': Accepts only digits (0-9)
 * - 'A': Accepts only letters (a-z, A-Z)
 * - 'N': Accepts digits, letters, and spaces
 * - 'M': Accepts letters and spaces
 * - 'X': Accepts any character
 * - 'E': Accepts valid email characters
 * - 'P': Accepts any non-whitespace character
 * - 'D': Accepts digits, letters (including accented), and spaces
 */
const tokenDefinitions = {
    '#': { test: char => /[0-9]/.test(char) },
    A: { test: char => /[a-zA-Z]/.test(char) },
    N: { test: char => /[0-9a-zA-Z\s]/.test(char) },
    M: { test: char => /[a-zA-Z\s]/.test(char) },
    X: { test: _char => true },
    E: { test: char => /[a-zA-Z0-9!#$%&'*+/=?^_`{|}~\.\-@]/.test(char) },
    P: { test: char => /\S/.test(char) },
    D: { test: char => /[0-9a-zA-Z\u00C0-\u00FF\s]/.test(char) }
};

function selectBestMask(value, tokenList) {
    const digitCount = value.replace(/\D/g, '').length;

    return tokenList.reduce((best, current) => {
        const bestLength = best.filter(t => t.type === 'mask').length;
        const currentLength = current.filter(t => t.type === 'mask').length;

        const bestDiff = Math.abs(bestLength - digitCount);
        const currentDiff = Math.abs(currentLength - digitCount);

        return currentDiff < bestDiff ? current : best;
    });
}

function parseMask(mask) {
    const availableTokens = Object.keys(tokenDefinitions).join('');
    const repeatRegex = new RegExp(
        `^([${availableTokens}])\\.repeat\\((\\d+)\\)$`
    );

    if (mask?.includes('repeat')) {
        const match = mask.match(repeatRegex);
        if (match) {
            const tokenChar = match[1];
            const count = parseInt(match[2], 10);
            return Array.from({ length: count }, () => ({
                type: 'mask',
                char: tokenChar,
                optional: false
            }));
        }
    }

    const tokens = [];

    for (let i = 0; i < mask.length; i++) {
        const char = mask[i];
        if (char === '?') {
            if (
                tokens.length > 0 &&
                tokens[tokens.length - 1].type === 'mask'
            ) {
                tokens[tokens.length - 1].optional = true;
            }
            continue;
        }
        if (tokenDefinitions[char]) {
            tokens.push({ type: 'mask', char, optional: false });
        } else {
            tokens.push({ type: 'literal', value: char });
        }
    }
    return tokens;
}

function applyMask(value, tokensOrList) {
    const tokens =
        Array.isArray(tokensOrList) && Array.isArray(tokensOrList[0])
            ? selectBestMask(value, tokensOrList)
            : tokensOrList;

    let result = '';
    let valueIndex = 0;

    for (let i = 0; i < tokens.length; i++) {
        const token = tokens[i];
        if (token.type === 'literal') {
            if (result.length > 0) {
                result += token.value;
            }
            continue;
        }

        if (valueIndex >= value.length) break;

        const currentChar = value[valueIndex];

        if (tokenDefinitions[token.char].test(currentChar)) {
            result += currentChar;
            valueIndex++;
        } else {
            valueIndex++;
            i--;
        }
    }

    return result;
}

const mask = {
    bind(el, binding) {
        const maskInput = binding.value;
        const maskList = Array.isArray(maskInput) ? maskInput : [maskInput];
        const parsedMasks = maskList?.map(parseMask);

        const onInput = e => {
            const input = e.target;
            if (input._maskProcessing) return;

            input._maskProcessing = true;
            
            const cleanValue = input.value.replace(/[^0-9]/g, '');
            const maskedValue = applyMask(cleanValue, parsedMasks);

            if (maskedValue !== input.value) {
                input.value = maskedValue;
                input.dispatchEvent(new Event('input', { bubbles: true }));
            }

            input._maskProcessing = false;
        };

        el._maskInputListener = onInput;
        el.addEventListener('input', onInput);
    },

    unbind(el) {
        if (el._maskInputListener) {
            el.removeEventListener('input', el._maskInputListener);
            delete el._maskInputListener;
        }
    }
};
export default mask;
